/**
 * API utility functions for handling different environments
 */

import * as Sentry from "@sentry/react";
import { resetPosthogIdentity } from './posthogIdentity';

// Import resetAuthCache function (will be available after AuthWrapper is loaded)
let resetAuthCache: (() => void) | null = null;
if (typeof window !== 'undefined') {
    // Dynamically import to avoid circular dependency
    import('../components/AuthWrapper')
        .then((module) => {
            resetAuthCache = module.resetAuthCache;
        })
        .catch(() => {
            // Ignore import errors
        });
}

/**
 * Get the base URL for API calls
 * In development: returns direct backend URL (bypasses rewrites due to auth header issues)
 * In production: returns the full API base URL
 */
export function getApiBaseUrl() {
    // In development, use direct backend URL to avoid rewrite header issues
    if (import.meta.env.DEV) {
        return import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    }

    // In production, use the full API URL from environment variables
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

    if (!apiBaseUrl) {
        console.error('VITE_API_BASE_URL environment variable is not set');
        throw new Error(
            'VITE_API_BASE_URL environment variable is required in production',
        );
    }

    return apiBaseUrl;
}

/**
 * Build a complete API URL
 * @param {string} path - API path (e.g., '/api/invoices')
 * @returns {string} Complete URL
 */
export function buildApiUrl(path: string): string {
    const baseUrl = getApiBaseUrl();

    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    return `${baseUrl}${normalizedPath}`;
}

/**
 * Get access token from localStorage
 */
function getAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('access_token');
}

/**
 * Get refresh token from localStorage
 */
function getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('refresh_token');
}

/**
 * Set tokens in localStorage
 */
function setTokens(accessToken: string, refreshToken: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
}

/**
 * Clear tokens from localStorage
 */
function clearTokens(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
}

/**
 * Refresh access token using refresh token from localStorage
 */
async function refreshAccessToken(): Promise<boolean> {
    try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) return false;

        const refreshUrl = buildApiUrl('/api/auth/refresh');
        const resp = await fetch(refreshUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (resp.ok) {
            const data = await resp.json();
            setTokens(data.access_token, data.refresh_token);
            return true;
        }

        // Refresh failed, clear tokens
        clearTokens();
        return false;
    } catch (e) {
        clearTokens();
        return false;
    }
}


/**
 * Enhanced fetch function that automatically handles API URLs and authentication
 * @param {string} path - API path
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export async function apiFetch(path: string, options: RequestInit = {}) {
    const url = buildApiUrl(path);
    const method = (options?.method || 'GET').toString().toUpperCase();
    const spanPath = path.startsWith('/') ? path : `/${path}`;

    // Add default headers only if not FormData
    const defaultHeaders = {} as Record<string, string>;

    // Don't set Content-Type for FormData, let the browser handle it
    if (!(options.body instanceof FormData)) {
        defaultHeaders['Content-Type'] = 'application/json';
    }

    // Add Authorization header if token exists
    const accessToken = getAccessToken();
    if (accessToken) {
        defaultHeaders['Authorization'] = `Bearer ${accessToken}`;
    }

    const mergedOptions = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    } as RequestInit & { headers: Record<string, string> };

    return Sentry.startSpan(
        { op: 'http.client', name: `${method} ${spanPath}` },
        async (span) => {
            try {
                span?.setAttribute('http.url', url);
                span?.setAttribute('http.method', method);

                const response = await fetch(url, mergedOptions);

                span?.setAttribute('http.status_code', response.status);

                // Handle 401 Unauthorized - try refresh, then redirect if needed
                if (response.status === 401 && typeof window !== 'undefined') {
                    // Attempt token refresh once
                    const refreshed = await refreshAccessToken();
                    if (refreshed) {
                        // Rebuild headers with new token
                        const newAccessToken = getAccessToken();
                        const retryHeaders = { ...mergedOptions.headers };
                        if (newAccessToken) {
                            retryHeaders['Authorization'] = `Bearer ${newAccessToken}`;
                        }

                        // Retry original request with new token
                        const retryResponse = await fetch(url, {
                            ...mergedOptions,
                            headers: retryHeaders
                        });
                        span?.setAttribute('http.retry_status_code', retryResponse.status);
                        if (retryResponse.status !== 401) {
                            return retryResponse;
                        }
                    }

                    // Refresh failed or still unauthorized
                    if (resetAuthCache) {
                        resetAuthCache();
                    }
                    Sentry.setUser(null);
                    resetPosthogIdentity();

                    // Check if we should skip redirect
                    const skipRedirect = (options as any).skipRedirect;
                    if (skipRedirect) {
                        return response;
                    }

                    try {
                        const path = window.location?.pathname || '';
                        const isSuper = path.startsWith('/superadmin');
                        const loginPath = isSuper ? '/superadmin-login' : '/login';

                        // Only redirect if we are not already on the login page
                        if (path !== loginPath) {
                            window.location.href = loginPath;
                        }
                    } catch {
                        // Fallback if window.location access fails
                        if (window.location.pathname !== '/login') {
                            window.location.href = '/login';
                        }
                    }
                }

                return response;
            } catch (error) {
                try { span?.setAttribute('error', true as unknown as string); } catch { }
                Sentry.captureException(error);
                throw error;
            }
        }
    );
}

/**
 * Enhanced fetch function with extended timeout for long-running operations
 * @param {string} path - API path
 * @param {RequestInit} options - Fetch options
 * @param {number} timeoutMs - Timeout in milliseconds (default: 15 minutes)
 * @returns {Promise<Response>} Fetch response
 */
export async function apiFetchWithTimeout(
    path: string,
    options: RequestInit = {},
    timeoutMs = 15 * 60 * 1000,
) {
    const url = buildApiUrl(path);
    const method = (options?.method || 'GET').toString().toUpperCase();
    const spanPath = path.startsWith('/') ? path : `/${path}`;

    // Add default headers only if not FormData
    const defaultHeaders = {} as Record<string, string>;

    // Don't set Content-Type for FormData, let the browser handle it
    if (!(options?.body instanceof FormData)) {
        defaultHeaders['Content-Type'] = 'application/json';
    }

    // Add Authorization header if token exists
    const accessToken = getAccessToken();
    if (accessToken) {
        defaultHeaders['Authorization'] = `Bearer ${accessToken}`;
    }

    const mergedOptions = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...(options?.headers || {}),
        },
        credentials: 'include',
    } as RequestInit & { headers: Record<string, string> };

    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    return Sentry.startSpan(
        { op: 'http.client', name: `${method} ${spanPath}` },
        async (span) => {
            try {
                span?.setAttribute('http.url', url);
                span?.setAttribute('http.method', method);
                span?.setAttribute('http.timeout_ms', timeoutMs);

                const response = await fetch(url, {
                    ...mergedOptions,
                    signal: controller.signal,
                });

                clearTimeout(timeoutId);

                span?.setAttribute('http.status_code', response.status);

                // Handle 401 Unauthorized - try refresh, then redirect if needed
                if (response.status === 401 && typeof window !== 'undefined') {
                    const refreshed = await refreshAccessToken();
                    if (refreshed) {
                        // Rebuild headers with new token
                        const newAccessToken = getAccessToken();
                        const retryHeaders = { ...mergedOptions.headers };
                        if (newAccessToken) {
                            retryHeaders['Authorization'] = `Bearer ${newAccessToken}`;
                        }

                        // Retry original request with new token
                        const retryResponse = await fetch(url, {
                            ...mergedOptions,
                            headers: retryHeaders,
                            signal: controller.signal
                        });
                        span?.setAttribute('http.retry_status_code', retryResponse.status);
                        if (retryResponse.status !== 401) {
                            clearTimeout(timeoutId);
                            return retryResponse;
                        }
                    }

                    if (resetAuthCache) {
                        resetAuthCache();
                    }
                    Sentry.setUser(null);
                    resetPosthogIdentity();
                    try {
                        const path = window.location?.pathname || '';
                        const isSuper = path.startsWith('/superadmin');
                        window.location.href = isSuper ? '/superadmin-login' : '/login';
                    } catch {
                        window.location.href = '/login';
                    }
                }

                return response;
            } catch (error: any) {
                clearTimeout(timeoutId);

                if (error && (error as any).name === 'AbortError') {
                    try { span?.setAttribute('aborted', true as unknown as string); } catch { }
                    throw new Error(`Request timed out after ${timeoutMs / 1000} seconds`);
                }

                try { span?.setAttribute('error', true as unknown as string); } catch { }
                Sentry.captureException(error);
                throw error;
            }
        }
    );
}

/**
 * Get file URL for uploaded files
 * @param {string} filePath - File path from the backend
 * @returns {string|null} Complete file URL
 */
export function getFileUrl(filePath) {
    if (!filePath) return null;

    // Convert absolute path to relative URL
    const relativePath = filePath.replace(/^.*uploads\//, '');

    // In development, use rewrite
    if (import.meta.env.DEV) {
        return `/files/${relativePath}`;
    }

    // In production, use full URL
    const baseUrl =
        import.meta.env.VITE_API_BASE_URL || 'https://cafe.sillyseason.me';
    return `${baseUrl}/files/${relativePath}`;
}

/**
 * Check if user has a valid access token in localStorage.
 * Note: This only checks for token existence, not validity.
 * AuthWrapper performs the actual server-side validation.
 */
export function isAuthenticated() {
    return !!getAccessToken();
}

/**
 * Log out the current user
 */
export async function logout() {
    if (typeof window !== 'undefined') {
        try {
            // Use apiFetch to include Authorization header
            await apiFetch('/api/auth/logout', { method: 'POST' });
        } catch { }

        // Clear tokens from localStorage
        clearTokens();

        // Reset auth cache
        if (resetAuthCache) {
            resetAuthCache();
        }

        // Unset Sentry user on logout
        Sentry.setUser(null);
        resetPosthogIdentity();

        window.location.href = '/login';
    }
}
